import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';
import {
  ExportMasterListView,
  MasterListJournal,
  MasterListJournalHistoryResponse,
  MasterListJournalRequestBody,
  MasterListStatusResponse,
} from '../../types/MasterList.types';
import { masterlistCheckQueryKey } from './useMasterlistCheck';

export const useGetMasterList = () => {
  const { makeApiRequest } = useApi();

  const { data, isLoading, error } = useQuery<MasterListJournal[]>({
    queryKey: ['masterList'],
    queryFn: () => makeApiRequest<MasterListJournal[]>('/masterlist', 'GET'),
  });

  return {
    masterList: data ?? [],
    isMasterListLoading: isLoading,
    masterListError: error,
  };
};

export const useExportMasterList = () => {
  const { downloadFile } = useApi();
  return useCallback(
    ({
      view,
      search,
      publishers = [],
      imprints = [],
      publishingModels = [],
      statuses = [],
      licenses = [],
    }: {
      view?: ExportMasterListView;
      search?: string;
      publishers?: string[];
      imprints?: string[];
      publishingModels?: string[];
      statuses?: string[];
      licenses?: string[];
    } = {}) => {
      const params = new URLSearchParams();
      if (view) params.set('view', view);
      if (search?.trim()) params.set('search', search.trim());
      publishers.forEach((p) => params.append('publishers', p));
      imprints.forEach((i) => params.append('imprints', i));
      publishingModels.forEach((m) => params.append('publishing_models', m));
      statuses.forEach((s) => params.append('statuses', s));
      licenses.forEach((l) => params.append('licenses', l));

      const date = new Date().toISOString().split('T')[0];
      return downloadFile(
        `/masterlist/export?${params.toString()}`,
        `masterlist_${date}.xlsx`
      );
    },
    [downloadFile]
  );
};

export const useMasterListStatus = () => {
  const { makeApiRequest } = useApi();

  const { data } = useQuery<MasterListStatusResponse>({
    queryKey: ['masterListStatus'],
    queryFn: () =>
      makeApiRequest<MasterListStatusResponse>('/masterlist/status', 'GET'),
  });

  return {
    pendingChanges: data,
    hasPendingChanges: (data?.pending_changes ?? 0) > 0,
  };
};

export const useUpdateMasterList = () => {
  const { makeApiRequest } = useApi();
  const queryClient = useQueryClient();

  const { mutateAsync: updateMasterList } = useMutation({
    mutationFn: (data: MasterListJournalRequestBody) =>
      makeApiRequest<MasterListStatusResponse>(
        '/masterlist/changeset',
        'POST',
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterListStatus'] });
      queryClient.invalidateQueries({ queryKey: ['masterList'] });
      queryClient.invalidateQueries({ queryKey: ['masterListHistory'] });
    },
  });

  return {
    updateMasterList,
  };
};

export const useUpdateMasterListChangeset = () => {
  const { makeApiRequest } = useApi();
  const queryClient = useQueryClient();

  const { mutateAsync: updateMasterListChangeset } = useMutation({
    mutationFn: ({
      itemId,
      delta,
      change_message,
    }: {
      itemId: string;
      delta: MasterListJournalRequestBody['delta'];
      change_message: string;
    }) =>
      makeApiRequest<MasterListStatusResponse>(
        `/masterlist/changeset/${itemId}`,
        'PUT',
        { delta, change_message }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterListStatus'] });
      queryClient.invalidateQueries({ queryKey: ['masterList'] });
      queryClient.invalidateQueries({ queryKey: ['masterListHistory'] });
    },
  });

  return { updateMasterListChangeset };
};

export const useDeleteMasterListChangeset = () => {
  const { makeApiRequest } = useApi();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteChangeset } = useMutation({
    mutationFn: (changesetId: string) =>
      makeApiRequest<MasterListStatusResponse>(
        `/masterlist/changeset/${changesetId}`,
        'DELETE'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterListStatus'] });
      queryClient.invalidateQueries({ queryKey: ['masterList'] });
    },
  });

  return { deleteChangeset };
};

export const useVerifyMasterList = () => {
  const { makeApiRequest } = useApi();
  const queryClient = useQueryClient();

  const { mutateAsync: verifyMasterList } = useMutation({
    mutationFn: () =>
      makeApiRequest<MasterListStatusResponse>(
        '/masterlist/changeset/verify',
        'POST'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterListStatus'] });
      queryClient.invalidateQueries({ queryKey: ['masterList'] });
      queryClient.invalidateQueries({ queryKey: ['masterListHistory'] });
      // Every agreement's check compares against the applied masterlist.
      queryClient.invalidateQueries({ queryKey: masterlistCheckQueryKey() });
    },
  });

  return { verifyMasterList };
};

export const useRevertMasterList = () => {
  const { makeApiRequest } = useApi();
  const queryClient = useQueryClient();

  const { mutateAsync: revertMasterList } = useMutation({
    mutationFn: (journalId: string) =>
      makeApiRequest<MasterListStatusResponse>(
        `/masterlist/${journalId}/changeset`,
        'DELETE'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterListStatus'] });
      queryClient.invalidateQueries({ queryKey: ['masterList'] });
    },
  });

  return {
    revertMasterList,
  };
};

export const useGetMasterListHistory = (journalId: string) => {
  const { makeApiRequest } = useApi();
  const url = `/masterlist/${journalId}/changes`;

  const { data } = useQuery<MasterListJournalHistoryResponse>({
    queryKey: ['masterListHistory', journalId],
    queryFn: () => makeApiRequest<MasterListJournalHistoryResponse>(url, 'GET'),
    enabled: !!journalId,
    refetchOnMount: 'always',
  });

  return {
    masterListHistory: data ?? [],
  };
};
